import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, InlineLink, PageAlert, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { BOLETO } from '@app/constants';
import AlertSectionLayout from '@app/components/AlertSectionLayout';
import { refetchBillingInfo, Route, selectBillingInfo, selectLocale, selectUser, setBillingNav } from '@app/redux';
import { selectConfig } from '@app/redux/config';
import {
  initiatePayment,
  resetPayments,
  retrieveTransactionId,
  selectPayments,
  setPayments,
} from '@app/redux/payments';
import { scrollToTop } from '@app/redux/utils';

import { Invoice, Locale, VppError } from '@mfe/shared/schema-types';

import Card from '../../components/card';
import Divider from '../../components/divider';
import Gradient from '../../components/gradient';
import { TitleCard } from '../../components/title';
import DifferentMethod from '../different-method';
import PaymentAmount, { AmountType } from './payment-amount';
import PaymentButtons from './payment-buttons';
import PaymentMethod, { MethodType } from './payment-method';
import PaymentStatus from './payment-status';
import { useNetworkStatus } from './useNetworkStatus';
import {
  handleCommonVppMessages,
  handleOtpMessages,
  iframeProps,
  NO_INTERNET_CONNECTION,
  parseVppEvent,
  postMessageToVPS,
  VppEventCode,
} from '../utils';

import styles from '../vpp.module.scss';

const INIT_PAYMENT_MESSAGE = JSON.stringify({
  initConnection: true,
  pageFunction: 'Payment',
});

const OneTimePayment = ({ invoice }: { invoice?: Invoice | null }) => {
  const {
    billingInfo: { autoPay, billingAccount },
  } = useSelector(selectBillingInfo);
  const {
    user: { productInstanceStatus },
  } = useSelector(selectUser);
  const { locale } = useSelector(selectLocale);

  const { vppUrl } = useSelector(selectConfig);
  const payments = useSelector(selectPayments);
  const dispatch = useDispatch();
  const isOffline = useNetworkStatus();
  const { url, paymentMethod, height, txnAmount, frameReady, submit, success, error, saveOTP, loading } = payments;
  const validTxnAmount = txnAmount >= 5;

  useLayoutEffect(() => {
    dispatch(resetPayments());
  }, [dispatch]);

  const balance = billingAccount?.balance?.value ?? 0;
  const [amountType, setAmountType] = useState(() => (balance <= 0 ? AmountType.other : AmountType.balance));

  const hasValidAutoPay = autoPay?.paymentMethod && autoPay.paymentMethod !== BOLETO;

  const [methodType, setMethodType] = useState(() => (hasValidAutoPay ? MethodType.pof : MethodType.other));

  const hasSelectedNewPaymentMethod = methodType === MethodType.other;

  const disabled = submit || !validTxnAmount;
  const showPaymentButtons = methodType === MethodType.pof || !frameReady;
  const frameLoading = hasSelectedNewPaymentMethod && !frameReady;
  const disablePaymentButtons = disabled || frameLoading;
  const amount = locale.userLocale === Locale.PtBr ? invoice?.amountOwedOnInvoice?.value ?? txnAmount : txnAmount;

  const { t } = useTranslation();

  const onSubmit = useCallback(() => {
    dispatch(setPayments({ submit: true }));
    dispatch(initiatePayment(invoice));
  }, [dispatch, invoice]);

  const onCancel = useCallback(() => dispatch(setBillingNav({ route: Route.Home })), [dispatch]);

  const onSuccess = useCallback(() => {
    dispatch(refetchBillingInfo());
    dispatch(setPayments({ success: true }));
    dispatch(scrollToTop());
  }, [dispatch]);

  const onError = useCallback(
    (error?: VppError) => {
      dispatch(setPayments({ error, submit: false }));
      dispatch(scrollToTop());
    },
    [dispatch]
  );

  useEffect(() => {
    const handleVppIframeMessage = (event: MessageEvent) => {
      const eventData = parseVppEvent(event, vppUrl);

      const handleVppMessage = [VppEventCode.PAYMENT_TYPE_CHANGED, VppEventCode.PAY_BUTTON_CLICKED].includes(
        eventData?.eventCode as VppEventCode
      )
        ? handleOtpMessages
        : handleCommonVppMessages;

      handleVppMessage(event, { ...payments, onSuccess, onError, onCancel }, vppUrl, dispatch, isOffline);
    };
    window.addEventListener('message', handleVppIframeMessage);

    return () => window.removeEventListener('message', handleVppIframeMessage);
  }, [payments, vppUrl, onSuccess, onError, onCancel, dispatch, isOffline]);

  useEffect(() => {
    if (hasSelectedNewPaymentMethod) {
      dispatch(retrieveTransactionId(invoice));
    }
  }, [dispatch, hasSelectedNewPaymentMethod, invoice]);

  const handleCheckboxDifferentPaymentMethod = () => {
    if (disabled) {
      return;
    }

    setMethodType(hasSelectedNewPaymentMethod ? MethodType.pof : MethodType.other);
  };

  const noInternetConnectionError = error?.message === NO_INTERNET_CONNECTION;

  if (success) {
    return (
      <PaymentStatus
        accountStatus={productInstanceStatus}
        balance={billingAccount?.balance}
        txnAmount={txnAmount}
        paymentMethod={paymentMethod}
        saveOTP={saveOTP}
      />
    );
  }

  return (
    <div className={styles['wrapper']}>
      <div className={styles['paymentContainer']}>
        {error && (
          <AlertSectionLayout>
            <PageAlert
              title={t('VPP:error.oneTimePayment.title')}
              variant={'error'}
              description={<AlertDescription />}
              onClose={() => {
                dispatch(setPayments({ error: null }));
                dispatch(retrieveTransactionId(invoice));
              }}
            />
          </AlertSectionLayout>
        )}

        <Card className={styles['card']}>
          <Gradient />

          <div className={styles['container']}>
            <div className={styles['titleContainer']}>
              <Txt variant="heading4">{t('VPP:makeAPayment')}</Txt>
            </div>

            <div className={styles['amountContainer']}>
              <TitleCard title={t('VPP:paymentAmountTitle')}>
                <PaymentAmount
                  balance={billingAccount?.balance}
                  invoice={invoice}
                  amountType={amountType}
                  methodType={methodType}
                  setAmountType={setAmountType}
                />
              </TitleCard>
            </div>

            {hasValidAutoPay && (
              <div className={styles['methodContainer']}>
                <TitleCard title={t('VPP:paymentMethodTitle')}>
                  <PaymentMethod autoPay={autoPay} methodType={methodType} setMethodType={setMethodType} />
                </TitleCard>
              </div>
            )}

            {hasValidAutoPay && (
              <div className={styles['checkboxContainer']} data-cy="different-payment-method-checkbox">
                <Checkbox
                  id="different-payment-method"
                  checked={hasSelectedNewPaymentMethod}
                  onChange={handleCheckboxDifferentPaymentMethod}
                  label={
                    <Txt variant="bodySmallRegular" color="regular">
                      {t('VPP:differentPaymentMethod')}
                    </Txt>
                  }
                />
                {!validTxnAmount ? (
                  <Txt component="p" mt="8px" variant="tinyItalic" color="subtle">
                    {t('VPP:paymentMethodTip')}
                  </Txt>
                ) : null}
              </div>
            )}
            {validTxnAmount && (
              <div className={styles['differentMethodContainer']}>
                <div
                  style={{
                    display: hasSelectedNewPaymentMethod && !noInternetConnectionError ? 'flex' : 'none',
                  }}
                >
                  {loading && (
                    <DifferentMethod frameReady={frameReady}>
                      <iframe
                        title="One Time Payment"
                        height={height}
                        src={url}
                        onLoad={() => postMessageToVPS(INIT_PAYMENT_MESSAGE, url)}
                        {...iframeProps}
                      />
                    </DifferentMethod>
                  )}
                </div>
              </div>
            )}

            {showPaymentButtons && (
              <>
                <Divider />

                <div className={styles['footerContainer']}>
                  <PaymentButtons
                    txnAmount={amount}
                    submit={submit}
                    balance={billingAccount?.balance}
                    disabled={disablePaymentButtons}
                    onSubmit={onSubmit}
                    onCancel={onCancel}
                  />
                </div>
              </>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

const AlertDescription = () => {
  const { t } = useTranslation();

  return (
    <Txt variant="bodySmallRegular" color="subtle">
      {t('VPP:error.oneTimePayment.description')}{' '}
      <InlineLink
        style={{ whiteSpace: 'nowrap' }}
        variant="primary"
        href={`tel:${t('Global:callCustomerSupportPhoneNumber')}`}
      >
        {t('Global:callCustomerSupportPhoneNumberDisplay')}
      </InlineLink>
    </Txt>
  );
};

export default OneTimePayment;
